// Reference Shelf — a poster wall of the P0 "dual-coding" infographics. Each
// poster is a self-contained HTML-string builder in the same visual language as
// the verb-group boot diagrams (gradient cards, mono grid cells, red = the thing
// that changes / the trap, sage = the regular/default). Every poster links to its
// full handbook article so the gallery is both a memory aid and a table of
// contents. View signature mirrors the other views: (mountEl, params).
import { navigate } from "../router/router.js";

// ---- Poster 1: Ser vs Estar decision tree -------------------------------------
function serEstarPoster() {
  return `
    <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">Which "to be"? Follow the branch</p>
      <div class="flex flex-col items-center gap-2 text-sm">
        <div class="bg-white border-2 border-sage-300 rounded-lg px-4 py-2 font-bold text-sage-800">¿SER o ESTAR?</div>
        <div class="text-sage-300 text-lg leading-none">▾</div>
        <div class="grid sm:grid-cols-2 gap-3 w-full">
          <div class="bg-white border border-sage-200 rounded-lg p-3">
            <p class="text-center font-bold text-sage-800 mb-1">SER</p>
            <p class="text-center text-xs text-sage-500 mb-2">essence — who/what it <i>is</i></p>
            <div class="font-mono text-xs grid grid-cols-1 gap-0.5 text-sage-700">
              <div><b class="text-sage-900">D</b>escription — Es alto.</div>
              <div><b class="text-sage-900">O</b>ccupation — Soy médico.</div>
              <div><b class="text-sage-900">C</b>haracteristic — Es amable.</div>
              <div><b class="text-sage-900">T</b>ime / date — Son las dos.</div>
              <div><b class="text-sage-900">O</b>rigin — Es de Perú.</div>
              <div><b class="text-sage-900">R</b>elationship — Es mi madre.</div>
            </div>
            <p class="text-center text-xs text-sage-400 mt-2 italic">mnemonic: <b>DOCTOR</b></p>
          </div>
          <div class="bg-red-50 border border-red-200 rounded-lg p-3">
            <p class="text-center font-bold text-red-700 mb-1">ESTAR</p>
            <p class="text-center text-xs text-sage-500 mb-2">state — how/where it <i>is</i> right now</p>
            <div class="font-mono text-xs grid grid-cols-1 gap-0.5 text-sage-700">
              <div><b class="text-red-700">P</b>osition — Está sentado.</div>
              <div><b class="text-red-700">L</b>ocation — Está en casa.</div>
              <div><b class="text-red-700">A</b>ction (-ndo) — Está comiendo.</div>
              <div><b class="text-red-700">C</b>ondition — Está enfermo.</div>
              <div><b class="text-red-700">E</b>motion — Está feliz.</div>
            </div>
            <p class="text-center text-xs text-sage-400 mt-2 italic">mnemonic: <b>PLACE</b></p>
          </div>
        </div>
        <p class="text-xs text-sage-500 mt-1 italic text-center max-w-md">Quick test: would it still be true next year? → <b>ser</b>. Could it change by tomorrow? → <b>estar</b>.</p>
      </div>
    </div>`;
}

// ---- Poster 2: Por vs Para map ------------------------------------------------
function porParaPoster() {
  return `
    <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">One English "for", two Spanish words</p>
      <div class="grid sm:grid-cols-2 gap-3 text-sm">
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="text-center font-bold text-sage-800 mb-2">POR <span class="font-normal text-xs text-sage-400">— looking back / through</span></p>
          <div class="font-mono text-xs space-y-1 text-sage-700">
            <div><b class="text-sage-900">cause / reason</b><br>Gracias por la ayuda.</div>
            <div><b class="text-sage-900">exchange</b><br>Pagué 10 € por el libro.</div>
            <div><b class="text-sage-900">duration</b><br>Estudié por dos horas.</div>
            <div><b class="text-sage-900">movement through</b><br>Camino por el parque.</div>
            <div><b class="text-sage-900">on behalf of</b><br>Lo hago por ti.</div>
          </div>
        </div>
        <div class="bg-red-50 border border-red-200 rounded-lg p-3">
          <p class="text-center font-bold text-red-700 mb-2">PARA <span class="font-normal text-xs text-sage-400">— looking forward / aim</span></p>
          <div class="font-mono text-xs space-y-1 text-sage-700">
            <div><b class="text-red-700">purpose / goal</b><br>Estudio para aprender.</div>
            <div><b class="text-red-700">recipient</b><br>Es para ti.</div>
            <div><b class="text-red-700">deadline</b><br>Para el lunes.</div>
            <div><b class="text-red-700">destination</b><br>Salgo para Madrid.</div>
            <div><b class="text-red-700">opinion</b><br>Para mí, es fácil.</div>
          </div>
        </div>
      </div>
      <p class="text-xs text-sage-500 mt-3 italic text-center">Arrow test: <b>por</b> points <b>backward</b> (the motive); <b>para</b> points <b>forward</b> (the destination).</p>
    </div>`;
}

// ---- Poster 3: Gender-ending cheat sheet --------------------------------------
function genderPoster() {
  return `
    <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">Predict a noun's gender from its ending</p>
      <div class="grid sm:grid-cols-2 gap-3 text-sm">
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="text-center font-bold text-sage-800 mb-2">usually <span class="text-sage-500">el</span> (masculine)</p>
          <div class="flex flex-wrap gap-1.5 justify-center font-mono text-xs">
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-o · libro</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-or · color</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-aje · viaje</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-án · pan</span>
          </div>
        </div>
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="text-center font-bold text-sage-800 mb-2">usually <span class="text-sage-500">la</span> (feminine)</p>
          <div class="flex flex-wrap gap-1.5 justify-center font-mono text-xs">
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-a · casa</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-ción · canción</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-sión · misión</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-dad · ciudad</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-tad · libertad</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-umbre · costumbre</span>
          </div>
        </div>
      </div>
      <div class="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
        <p class="text-center font-bold text-red-700 mb-2">⚠ the traps — learn these by heart</p>
        <div class="flex flex-wrap gap-1.5 justify-center font-mono text-xs">
          <span class="bg-red-100 border border-red-300 text-red-700 px-2 py-1 rounded">el día</span>
          <span class="bg-red-100 border border-red-300 text-red-700 px-2 py-1 rounded">el problema</span>
          <span class="bg-red-100 border border-red-300 text-red-700 px-2 py-1 rounded">el mapa</span>
          <span class="bg-red-100 border border-red-300 text-red-700 px-2 py-1 rounded">el idioma</span>
          <span class="bg-red-100 border border-red-300 text-red-700 px-2 py-1 rounded">la mano</span>
          <span class="bg-red-100 border border-red-300 text-red-700 px-2 py-1 rounded">la foto</span>
        </div>
        <p class="text-center text-xs text-sage-500 mt-2 italic">Greek-origin <b>-ma</b> words are masculine; <i>mano</i> &amp; <i>foto</i> end in -o/-a but flip.</p>
      </div>
    </div>`;
}

// ---- Poster 4: Sound–spelling chart -------------------------------------------
function soundsPoster() {
  return `
    <div class="bg-gradient-to-br from-sage-50 to-white border border-sage-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">Letter → sound (the ones that trip up English speakers)</p>
      <p class="text-xs text-sage-500 mb-1.5">The five vowels — pure &amp; constant</p>
      <div class="grid grid-cols-5 gap-1.5 text-center font-mono text-sm mb-3">
        <div class="bg-white border border-sage-200 rounded-lg p-2"><b class="text-sage-900">a</b><br><span class="text-xs text-sage-500">ah</span></div>
        <div class="bg-white border border-sage-200 rounded-lg p-2"><b class="text-sage-900">e</b><br><span class="text-xs text-sage-500">eh</span></div>
        <div class="bg-white border border-sage-200 rounded-lg p-2"><b class="text-sage-900">i</b><br><span class="text-xs text-sage-500">ee</span></div>
        <div class="bg-white border border-sage-200 rounded-lg p-2"><b class="text-sage-900">o</b><br><span class="text-xs text-sage-500">oh</span></div>
        <div class="bg-white border border-sage-200 rounded-lg p-2"><b class="text-sage-900">u</b><br><span class="text-xs text-sage-500">oo</span></div>
      </div>
      <p class="text-xs text-sage-500 mb-1.5">The tricky consonants</p>
      <div class="grid sm:grid-cols-2 gap-x-4 gap-y-1 font-mono text-xs text-sage-700">
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">c</b> + e/i</span><span>soft <i>s</i> — cena</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b>c</b> + a/o/u</span><span>hard <i>k</i> — casa</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">g</b> + e/i</span><span>throat <i>h</i> — gente</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b>g</b> + a/o/u</span><span>hard <i>g</i> — gato</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">j</b></span><span>throat <i>h</i> — jefe</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">h</b></span><span><b>silent</b> — hola</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">ñ</b></span><span><i>ny</i> — niño</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">ll</b></span><span><i>y</i> — llamar</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b>qu</b></span><span>hard <i>k</i> — queso</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">z</b></span><span><i>s</i> — zapato</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">v</b></span><span>soft <i>b</i> — vino</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b>rr</b> / r-</span><span>rolled — perro</span></div>
      </div>
      <p class="text-xs text-sage-500 mt-3 italic">Red letters are the ones that differ most from English. Master those and your accent jumps.</p>
    </div>`;
}

// ---- Poster 5: Accent-rules flowchart -----------------------------------------
function accentPoster() {
  return `
    <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">Does the word need a written accent?</p>
      <div class="flex flex-col gap-2 text-sm">
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="font-bold text-sage-800 mb-1">Step 1 — the two default rules (no accent needed)</p>
          <div class="font-mono text-xs space-y-1 text-sage-700">
            <div>ends in <b>vowel, n, s</b> → stress the <b>2nd-to-last</b> · ca<b class="text-sage-900">sa</b>, ha<b class="text-sage-900">blan</b></div>
            <div>ends in any <b>other consonant</b> → stress the <b>last</b> · ha<b class="text-sage-900">blar</b>, ciu<b class="text-sage-900">dad</b></div>
          </div>
        </div>
        <div class="text-sage-300 text-lg leading-none text-center">▾</div>
        <div class="bg-red-50 border border-red-200 rounded-lg p-3">
          <p class="font-bold text-red-700 mb-1">Step 2 — if the real stress breaks the rule, write the accent</p>
          <div class="font-mono text-xs grid sm:grid-cols-2 gap-x-4 gap-y-0.5 text-sage-700">
            <div>ca<b class="text-red-700">fé</b> · so<b class="text-red-700">fá</b></div>
            <div>in<b class="text-red-700">glés</b> · <b class="text-red-700">ár</b>bol</div>
            <div><b class="text-red-700">lá</b>piz · <b class="text-red-700">mé</b>dico</div>
            <div>te<b class="text-red-700">lé</b>fono · <b class="text-red-700">mú</b>sica</div>
          </div>
        </div>
      </div>
      <div class="grid grid-cols-3 gap-2 mt-3 text-center text-xs font-mono">
        <div class="bg-white border border-sage-200 rounded-lg p-2"><b class="text-sage-800">agudas</b><br>stress last<br><span class="text-sage-400">café, salón</span></div>
        <div class="bg-white border border-sage-200 rounded-lg p-2"><b class="text-sage-800">llanas</b><br>stress 2nd-last<br><span class="text-sage-400">árbol, lápiz</span></div>
        <div class="bg-red-50 border border-red-200 rounded-lg p-2"><b class="text-red-700">esdrújulas</b><br>stress 3rd-last<br><span class="text-sage-400">always! médico</span></div>
      </div>
    </div>`;
}

// ---- Poster 6: Gustar construction diagram ------------------------------------
function gustarPoster() {
  return `
    <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">"Me gusta" = <i>it pleases me</i>, not "I like"</p>
      <div class="bg-white border border-sage-200 rounded-lg p-4">
        <div class="flex flex-wrap items-end justify-center gap-2 font-mono text-sm">
          <div class="text-center">
            <div class="bg-sage-50 border border-sage-200 text-sage-500 px-3 py-1.5 rounded-lg">(A mí)</div>
            <p class="text-xs text-sage-400 mt-1">optional</p>
          </div>
          <div class="text-center">
            <div class="bg-red-100 border-2 border-red-300 text-red-700 px-3 py-1.5 rounded-lg font-bold">me</div>
            <p class="text-xs text-sage-400 mt-1">who's pleased</p>
          </div>
          <div class="text-center">
            <div class="bg-red-100 border-2 border-red-300 text-red-700 px-3 py-1.5 rounded-lg font-bold">gusta / gustan</div>
            <p class="text-xs text-sage-400 mt-1">agrees with →</p>
          </div>
          <div class="text-center">
            <div class="bg-sage-50 border border-sage-200 text-sage-700 px-3 py-1.5 rounded-lg">el café / los libros</div>
            <p class="text-xs text-sage-400 mt-1">the real subject</p>
          </div>
        </div>
      </div>
      <div class="grid sm:grid-cols-2 gap-3 mt-3 text-sm">
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="font-bold text-sage-800 text-xs mb-1.5">who is pleased (indirect object)</p>
          <div class="font-mono text-xs grid grid-cols-2 gap-x-3 gap-y-0.5 text-sage-700">
            <div>me — I</div><div>nos — we</div>
            <div>te — you</div><div>os — you all</div>
            <div>le — he/she</div><div>les — they</div>
          </div>
        </div>
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="font-bold text-sage-800 text-xs mb-1.5">pick the verb form by the <i>thing</i></p>
          <div class="font-mono text-xs space-y-0.5 text-sage-700">
            <div>one thing → <b class="text-red-700">gusta</b> · Me gusta el café.</div>
            <div>many → <b class="text-red-700">gustan</b> · Me gustan los libros.</div>
            <div>an action → <b class="text-red-700">gusta</b> · Me gusta bailar.</div>
          </div>
        </div>
      </div>
      <p class="text-xs text-sage-500 mt-3 italic text-center">Read it backwards: <i>Me gustan los libros</i> → "the books please me" → "I like the books."</p>
    </div>`;
}

// ---- Poster 7: Pretérito vs Imperfecto (photo vs film) ------------------------
function preteritoImperfectoPoster() {
  return `
    <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">Two past tenses — pick a <i>viewpoint</i>, not a time</p>
      <div class="grid sm:grid-cols-2 gap-3 text-sm">
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="text-center font-bold text-sage-800 mb-1">📷 PRETÉRITO</p>
          <p class="text-center text-xs text-sage-500 mb-2">a photo — one bounded, finished event</p>
          <div class="font-mono text-xs space-y-1 text-sage-700">
            <div><b class="text-sage-900">single done action</b><br>Comí a las dos.</div>
            <div><b class="text-sage-900">sequence of events</b><br>Llegó, comió y salió.</div>
            <div><b class="text-sage-900">the interruption</b><br>…cuando sonó el teléfono.</div>
          </div>
          <div class="flex flex-wrap gap-1 justify-center mt-2 font-mono text-[11px]">
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-1.5 py-0.5 rounded">ayer</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-1.5 py-0.5 rounded">de repente</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-1.5 py-0.5 rounded">anoche</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-1.5 py-0.5 rounded">una vez</span>
          </div>
        </div>
        <div class="bg-red-50 border border-red-200 rounded-lg p-3">
          <p class="text-center font-bold text-red-700 mb-1">🎬 IMPERFECTO</p>
          <p class="text-center text-xs text-sage-500 mb-2">a film rolling — ongoing, habitual, scenery</p>
          <div class="font-mono text-xs space-y-1 text-sage-700">
            <div><b class="text-red-700">background / scene</b><br>Era de noche y llovía.</div>
            <div><b class="text-red-700">habit / routine</b><br>Iba a la playa cada verano.</div>
            <div><b class="text-red-700">the thing interrupted</b><br>Yo leía cuando…</div>
          </div>
          <div class="flex flex-wrap gap-1 justify-center mt-2 font-mono text-[11px]">
            <span class="bg-red-100 border border-red-300 text-red-700 px-1.5 py-0.5 rounded">siempre</span>
            <span class="bg-red-100 border border-red-300 text-red-700 px-1.5 py-0.5 rounded">a menudo</span>
            <span class="bg-red-100 border border-red-300 text-red-700 px-1.5 py-0.5 rounded">todos los días</span>
            <span class="bg-red-100 border border-red-300 text-red-700 px-1.5 py-0.5 rounded">mientras</span>
          </div>
        </div>
      </div>
      <p class="text-xs text-sage-500 mt-3 italic text-center">Together: the <b>imperfecto</b> paints the scene; the <b>pretérito</b> drops in the event that moves the story.</p>
    </div>`;
}

// ---- Poster 8: RID object-pronoun order ---------------------------------------
function pronounOrderPoster() {
  return `
    <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">Stacking pronouns? They line up in <b>RID</b> order</p>
      <div class="bg-white border border-sage-200 rounded-lg p-4">
        <div class="flex flex-wrap items-end justify-center gap-2 font-mono text-sm">
          <div class="text-center">
            <div class="bg-sage-50 border border-sage-200 text-sage-700 px-3 py-1.5 rounded-lg font-bold">R</div>
            <p class="text-xs text-sage-400 mt-1">reflexive<br>se</p>
          </div>
          <div class="text-sage-300 text-lg leading-none pb-3">+</div>
          <div class="text-center">
            <div class="bg-red-100 border-2 border-red-300 text-red-700 px-3 py-1.5 rounded-lg font-bold">I</div>
            <p class="text-xs text-sage-400 mt-1">indirect<br>me te le nos os les</p>
          </div>
          <div class="text-sage-300 text-lg leading-none pb-3">+</div>
          <div class="text-center">
            <div class="bg-sage-50 border border-sage-200 text-sage-700 px-3 py-1.5 rounded-lg font-bold">D</div>
            <p class="text-xs text-sage-400 mt-1">direct<br>lo la los las</p>
          </div>
          <div class="text-sage-300 text-lg leading-none pb-3">+</div>
          <div class="text-center">
            <div class="bg-white border border-sage-300 text-sage-800 px-3 py-1.5 rounded-lg font-bold">verb</div>
          </div>
        </div>
      </div>
      <div class="grid sm:grid-cols-2 gap-3 mt-3 text-sm">
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="font-bold text-sage-800 text-xs mb-1.5">the le → se swap</p>
          <div class="font-mono text-xs space-y-0.5 text-sage-700">
            <div class="text-red-700">✗ le lo doy</div>
            <div class="text-sage-900">✓ <b>se lo</b> doy</div>
            <div class="text-sage-400 mt-1 not-italic">le/les + lo/la → <b>se</b> (avoids "le lo")</div>
          </div>
        </div>
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="font-bold text-sage-800 text-xs mb-1.5">where they sit</p>
          <div class="font-mono text-xs space-y-0.5 text-sage-700">
            <div><b class="text-sage-900">before</b> a conjugated verb · Me lo da.</div>
            <div><b class="text-red-700">attached</b> to infinitive · dármelo</div>
            <div><b class="text-red-700">attached</b> to a command · ¡Dámelo!</div>
          </div>
        </div>
      </div>
      <p class="text-xs text-sage-500 mt-3 italic text-center">Indirect always beats direct to the verb: <b>me lo</b>, <b>te la</b>, <b>se los</b> — never the reverse.</p>
    </div>`;
}

// ---- Poster 9: WEIRDO subjunctive triggers ------------------------------------
function weirdoPoster() {
  return `
    <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">What flips the verb into the subjunctive: <b>WEIRDO</b></p>
      <div class="grid sm:grid-cols-2 gap-2 text-sm">
        <div class="bg-white border border-sage-200 rounded-lg p-2.5 font-mono text-xs text-sage-700">
          <b class="text-red-700">W</b>ishes — querer / esperar que<br><span class="text-sage-500">Quiero que estudies.</span>
        </div>
        <div class="bg-white border border-sage-200 rounded-lg p-2.5 font-mono text-xs text-sage-700">
          <b class="text-red-700">E</b>motions — alegrarse / temer que<br><span class="text-sage-500">Me alegro de que vengas.</span>
        </div>
        <div class="bg-white border border-sage-200 rounded-lg p-2.5 font-mono text-xs text-sage-700">
          <b class="text-red-700">I</b>mpersonal — es necesario que<br><span class="text-sage-500">Es importante que lo sepas.</span>
        </div>
        <div class="bg-white border border-sage-200 rounded-lg p-2.5 font-mono text-xs text-sage-700">
          <b class="text-red-700">R</b>ecommendations — pedir / aconsejar que<br><span class="text-sage-500">Te pido que esperes.</span>
        </div>
        <div class="bg-white border border-sage-200 rounded-lg p-2.5 font-mono text-xs text-sage-700">
          <b class="text-red-700">D</b>oubt / Denial — dudar / no creer que<br><span class="text-sage-500">Dudo que sea verdad.</span>
        </div>
        <div class="bg-white border border-sage-200 rounded-lg p-2.5 font-mono text-xs text-sage-700">
          <b class="text-red-700">O</b>jalá — ojalá (que)<br><span class="text-sage-500">Ojalá llueva pronto.</span>
        </div>
      </div>
      <div class="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
        <p class="text-center font-bold text-red-700 text-xs mb-1.5">two subjects + <b>que</b> are the gate</p>
        <div class="font-mono text-xs text-center text-sage-700 space-y-0.5">
          <div>same subject → infinitive · Quiero <b class="text-sage-900">ir</b>.</div>
          <div>different subject → subjunctive · Quiero que <b class="text-red-700">vayas</b>.</div>
        </div>
      </div>
      <p class="text-xs text-sage-500 mt-3 italic text-center">Build it: take the <b>yo</b> present, drop -o, add the opposite vowel (-ar→-e, -er/-ir→-a).</p>
    </div>`;
}

// Poster registry — id, title, subtitle, builder, and the article slug it links to.
const POSTERS = [
  { id: "ser-estar", title: "Ser vs Estar", subtitle: "Which \"to be\" to use", build: serEstarPoster, slug: "ser-vs-estar" },
  { id: "por-para", title: "Por vs Para", subtitle: "The two \"for\"s by function", build: porParaPoster, slug: "por-vs-para" },
  { id: "gender", title: "Noun Gender Endings", subtitle: "Predict el/la + the traps", build: genderPoster, slug: "gender-agreement-of-nouns" },
  { id: "sounds", title: "Sound–Spelling Chart", subtitle: "Letter → sound", build: soundsPoster, slug: "pronunciation-and-the-alphabet" },
  { id: "accents", title: "Accent Rules", subtitle: "Where the written accent goes", build: accentPoster, slug: "stress-and-written-accents" },
  { id: "gustar", title: "Gustar Construction", subtitle: "The \"me gusta\" inversion", build: gustarPoster, slug: "gustar-and-similar-verbs" },
  { id: "preterito-imperfecto", title: "Pretérito vs Imperfecto", subtitle: "Photo vs film — the aspect choice", build: preteritoImperfectoPoster, slug: "a2-preterito-vs-imperfecto" },
  { id: "pronoun-order", title: "Object-Pronoun Order (RID)", subtitle: "Stacking me/te/se + lo/la", build: pronounOrderPoster, slug: "object-pronouns" },
  { id: "weirdo", title: "Subjunctive Triggers (WEIRDO)", subtitle: "What flips into the subjunctive", build: weirdoPoster, slug: "b1-present-subjunctive" },
];

export function referenceView(app, params = {}) {
  app.innerHTML = `
    <section>
      <h2 class="text-2xl font-bold text-sage-800 mb-1">Reference Shelf</h2>
      <p class="text-sage-600 text-sm mb-6 max-w-2xl">Every key distinction as a one-glance poster. Pair the picture with the rule — that's how it sticks. Tap any poster to read the full guide.</p>
      <div class="grid lg:grid-cols-2 gap-5" id="poster-wall"></div>
    </section>`;

  const wall = app.querySelector("#poster-wall");
  POSTERS.forEach((p) => {
    const card = document.createElement("div");
    card.id = `poster-${p.id}`;
    card.className = "scroll-mt-24";
    card.innerHTML = `
      <div class="flex items-baseline justify-between mb-2">
        <h3 class="font-bold text-lg text-sage-800">${p.title}</h3>
        <button data-slug="${p.slug}" class="poster-link text-xs font-medium text-sage-500 hover:text-sage-800">Read the full guide →</button>
      </div>
      <p class="text-xs text-sage-500 mb-2">${p.subtitle}</p>
      ${p.build()}
    `;
    wall.appendChild(card);
  });

  app.querySelectorAll(".poster-link").forEach((btn) =>
    btn.addEventListener("click", () => navigate("article", { slug: btn.dataset.slug }))
  );

  // Deep-link support: #/reference?poster=gustar scrolls that poster into view.
  if (params.poster && app.querySelector(`#poster-${params.poster}`)) {
    app.querySelector(`#poster-${params.poster}`).scrollIntoView({ behavior: "smooth", block: "start" });
  } else {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
}
